import React, { useEffect, useRef, useState, type JSX } from 'react';
import styled, { keyframes } from 'styled-components';

interface ILazyImage {
  src: string;
  alt: string;
  formatImage?: (src: string, width: number) => string;
  srcSet?: string;
  sizes?: string;
  placeholder?: string;
}

const placeholder =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkqAcAAIUAgUW0RjgAAAAASUVORK5CYII=';

const loaded = keyframes`
  0% { opacity: 0.1; }
  100% { opacity: 1; }
`;

const Image = styled.img<ILazyImage>`
  width: 100%;
  max-width: 100%;
  height: auto;

  &:not(.has-error) {
    animation: ${loaded} 300ms ease-in-out;
  }

  &.has-error {
    content: url(${placeholder});
  }
`;

const WIDTH_SIZE = [250, 600, 1024, 1200];

/**
 * @deprecated Use ResponsiveImage component instead
 */
const LazyImage = ({
  src,
  alt,
  formatImage,
  srcSet,
  sizes = '100vw',
}: ILazyImage): JSX.Element => {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const imageRef = useRef<HTMLImageElement>(
    null,
  ) as React.MutableRefObject<HTMLImageElement>;
  const defaultSizesSrcSet =
    formatImage &&
    WIDTH_SIZE.map((width) => `${formatImage(src, width)} ${width}w`).join(',');

  const sourceSet = (defaultSizesSrcSet ?? srcSet) || '';

  useEffect(() => {
    const currentImageRef = imageRef.current; // side effects fix. thx to eslintreact-hooks/exhaustive-deps
    let observer: IntersectionObserver;
    let didCancel = false;

    if (imageRef && imageSrc === placeholder) {
      if (IntersectionObserver) {
        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (
                !didCancel &&
                (entry.intersectionRatio > 0 || entry.isIntersecting)
              ) {
                // OneTrust cookie consent store `src` in `data-src` on load
                // and swap them when consent is given.
                // Without this, it restore `placeholder` content in `src`.
                imageRef.current.dataset['src'] = src;
                setImageSrc(src);
                currentImageRef.srcset = sourceSet;
              }
            });
          },
          {
            threshold: 0.01,
            rootMargin: '50%',
          },
        );

        observer.observe(currentImageRef);
      } else {
        imageRef.current.dataset['src'] = src;
        setImageSrc(src);
        currentImageRef.srcset = sourceSet;
      }
    }

    return () => {
      didCancel = true;
      if (observer && observer.unobserve) {
        observer.unobserve(currentImageRef);
      }
    };
  }, [imageRef, imageSrc, src]);

  const onLoad = (event: React.ChangeEvent<HTMLImageElement>) => {
    event.target.classList.add('loaded');
  };

  const onError = (event: React.ChangeEvent<HTMLImageElement>) => {
    event.target.classList.add('has-error');
  };

  return (
    <Image
      ref={imageRef}
      src={imageSrc}
      alt={alt}
      placeholder={placeholder}
      onLoad={onLoad}
      onError={onError}
      sizes={sizes}
    />
  );
};

export default LazyImage;
